class HeaderDrawer extends HTMLElement {
  constructor() {
    super();
    this.onResize = this.onResize.bind(this);
  }

  connectedCallback() {
    this.menuContainer = this.querySelector('.menu-drawer-container');
    this.mainDetailsToggle = this.querySelector('.header__icon--menu');
    this.productsSectionId = 'header-mega-menu-products';
    this.productsCache = new Map();
    this.productsPrefetch = new Set();

    if (this.mainDetailsToggle) {
      this.mainDetailsToggle.addEventListener('click', this.toggleDrawer.bind(this));
    }

    if (this.querySelector('.menu-drawer-v2')) {
      this.bindMobilePanels();
    }
  }

  toggleDrawer() {
    if (this.menuContainer.classList.contains('open')) {
      this.closeMenuDrawer();
    } else {
      this.openMenuDrawer();
    }
  }

  openMenuDrawer() {
    publish(PUB_SUB_EVENTS.modalToggle,  { display: false });
    
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset = this.borderOffset ?? (
      this.closest('.header-wrapper')?.classList.contains('header-wrapper--border-bottom') ? 1 : 0
    );

    if (this.header) {
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
      this.header.classList.add('menu-open');
    }

    setTimeout(() => {
      this.mainDetailsToggle?.classList.add('menu-opening');
    });

    this.menuContainer.classList.add('open');
    window.addEventListener('resize', this.onResize);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);

    if (this.querySelector('[data-accordion-default] .menu-drawer-v2__accordion-panel:not([hidden])')) {
      this.initRatingWidgets();
    }
  }

  closeMenuDrawer() {
    publish(PUB_SUB_EVENTS.modalToggle,  { display: true });

    if (this.header) this.header.classList.remove('menu-open');
    this.menuContainer.classList.remove('open');
    this.mainDetailsToggle.classList.remove('menu-opening');
    window.removeEventListener('resize', this.onResize);
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    this.resetMobilePanels();
  }

  onResize() {
    if (this.header) {
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    }
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  }

  bindMobilePanels() {
    this.panels = this.querySelectorAll('[data-panel]');
    this.openPanelButtons = this.querySelectorAll('[data-open-panel]');
    this.backPanelButtons = this.querySelectorAll('[data-back-panel]');
    this.linkButtons = this.querySelectorAll('[data-link-url]');
    this.categoryTriggers = this.querySelectorAll('.menu-drawer-v2__category-trigger');
    this.accordionTriggers = this.querySelectorAll('.menu-drawer-v2__accordion-trigger');
    this.utilityPanel = this.querySelector('[data-utility-panel]');
    this.utilityToggle = this.querySelector('[data-utility-panel-toggle]');
    this.utilityClose = this.querySelector('[data-utility-panel-close]');

    this.openPanelButtons.forEach((button) => {
      button.addEventListener('click', () => this.openPanel(button.dataset.openPanel));
    });

    this.backPanelButtons.forEach((button) => {
      button.addEventListener('click', () => this.openPanel(button.dataset.backPanel));
    });

    this.linkButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const url = button.dataset.linkUrl;
        if (url) window.location.href = url;
      });
    });

    this.categoryTriggers.forEach((trigger) => {
      trigger.addEventListener('pointerenter', () => {
        const url = trigger.dataset.productsUrl;
        if (url) this.prefetchDrawerProducts(url);
      });

      trigger.addEventListener('click', async () => {
        const item = trigger.closest('[data-category-item]');
        const panel = trigger.closest('[data-panel]');
        const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

        panel?.querySelectorAll('.menu-drawer-v2__category-trigger').forEach((panelTrigger) => {
          panelTrigger.setAttribute('aria-expanded', 'false');
        });
        panel?.querySelectorAll('.menu-drawer-v2__category-products').forEach((products) => {
          products.hidden = true;
        });

        if (isExpanded) return;

        const productsWrap = item?.querySelector('.menu-drawer-v2__category-products');
        const productsContainer = item?.querySelector('[data-products-container]');
        const productsUrl = trigger.dataset.productsUrl;

        if (!productsContainer || !productsUrl) {
          if (productsWrap) productsWrap.hidden = false;
          trigger.setAttribute('aria-expanded', 'true');
          return;
        }

        if (!this.productsCache.has(productsUrl)) {
          productsContainer.innerHTML =
            '<div class="header-v2__products-loading header-v2__products-loading--drawer" aria-hidden="true"></div>';
        }

        productsWrap.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        await this.loadDrawerProducts(productsUrl, productsContainer);
        this.scrollCategoryItemIntoView(item);
      });
    });

    this.accordionTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const item = trigger.closest('[data-accordion-item]');
        const panel = item?.querySelector('.menu-drawer-v2__accordion-panel');
        const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

        this.querySelectorAll('.menu-drawer-v2__accordion-trigger').forEach((otherTrigger) => {
          if (otherTrigger === trigger) return;
          otherTrigger.setAttribute('aria-expanded', 'false');
          const otherPanel = otherTrigger.closest('[data-accordion-item]')?.querySelector('.menu-drawer-v2__accordion-panel');
          if (otherPanel) otherPanel.hidden = true;
        });

        trigger.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
        if (panel) panel.hidden = isExpanded;
        if (!isExpanded) this.initRatingWidgets();
      });
    });

    if (this.utilityToggle) {
      this.utilityToggle.addEventListener('click', () => this.toggleUtilityPanel(true));
    }

    if (this.utilityClose) {
      this.utilityClose.addEventListener('click', () => this.toggleUtilityPanel(false));
    }
  }

  openPanel(panelId = 'root') {
    if (!this.panels || !this.panels.length) return;
    this.panels.forEach((panel) => panel.classList.remove('is-active'));
    const target = this.querySelector(`[data-panel="${panelId}"]`);
    if (target) {
      target.classList.add('is-active');
      if (target.dataset.mode === 'category') {
        this.prefetchCategoryPanel(target);
      }
    }
  }

  prefetchCategoryPanel(panel) {
    panel.querySelectorAll('[data-products-url]').forEach((trigger) => {
      this.prefetchDrawerProducts(trigger.dataset.productsUrl);
    });
  }

  prefetchDrawerProducts(productsUrl) {
    if (!productsUrl || this.productsCache.has(productsUrl) || this.productsPrefetch.has(productsUrl)) return;

    this.productsPrefetch.add(productsUrl);
    this.fetchDrawerProductsMarkup(productsUrl).catch(() => {
      this.productsPrefetch.delete(productsUrl);
    });
  }

  resetMobilePanels() {
    this.openPanel('root');
    this.querySelectorAll('.menu-drawer-v2__category-trigger').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });
    this.querySelectorAll('.menu-drawer-v2__category-products').forEach((products) => {
      products.hidden = true;
    });
    this.querySelectorAll('.menu-drawer-v2__accordion-item').forEach((item) => {
      const trigger = item.querySelector('.menu-drawer-v2__accordion-trigger');
      const panel = item.querySelector('.menu-drawer-v2__accordion-panel');
      const openByDefault = item.hasAttribute('data-accordion-default');
      if (trigger) trigger.setAttribute('aria-expanded', openByDefault ? 'true' : 'false');
      if (panel) panel.hidden = !openByDefault;
    });
    this.toggleUtilityPanel(false);
  }

  toggleUtilityPanel(open = false) {
    if (!this.utilityPanel || !this.utilityToggle) return;
    this.utilityPanel.hidden = !open;
    this.utilityToggle.classList.toggle('is-active', open);
  }

  async loadDrawerProducts(productsUrl, container) {
    if (!productsUrl || !container) return;

    const productsWrap = container.closest('.menu-drawer-v2__category-products');

    try {
      const markup = await this.fetchDrawerProductsMarkup(productsUrl);
      if (!markup) {
        container.innerHTML = '';
        return;
      }

      container.innerHTML = markup;
      this.hydrateDrawerProductImages(container);
      this.initRatingWidgets();
      this.initCategorySlider(productsWrap);
    } catch (error) {
      console.error('Header drawer products:', error);
      container.innerHTML = '';
    }
  }

  async fetchDrawerProductsMarkup(productsUrl) {
    if (this.productsCache.has(productsUrl)) {
      return this.productsCache.get(productsUrl);
    }

    let fetchPath = productsUrl;
    try {
      const parsedUrl = new URL(productsUrl, window.location.origin);
      fetchPath = `${parsedUrl.pathname}${parsedUrl.search}`;
    } catch (error) {
      fetchPath = productsUrl;
    }

    const separator = fetchPath.includes('?') ? '&' : '?';
    const fetchUrl = `${fetchPath}${separator}section_id=${this.productsSectionId}`;
    const response = await fetch(fetchUrl);
    if (!response.ok) throw new Error('Failed to load drawer products');

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const content = doc.getElementById('header-mega-menu-products');
    const markup = content ? content.innerHTML : '';

    this.productsCache.set(productsUrl, markup);
    return markup;
  }

  scrollCategoryItemIntoView(item) {
    if (!item) return;

    const panel = item.closest('[data-panel]');
    const scrollContainer =
      panel?.dataset.mode === 'category'
        ? panel.querySelector('.menu-drawer-v2__panel-list')
        : this.querySelector('.menu-drawer-v2');

    if (!scrollContainer) return;

    requestAnimationFrame(() => {
      const containerRect = scrollContainer.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const utility = this.querySelector('.menu-drawer-v2__utility');
      const utilityHeight = utility?.offsetHeight ?? 0;
      const visibleBottom = containerRect.bottom - utilityHeight - 8;

      if (itemRect.bottom > visibleBottom) {
        scrollContainer.scrollTop += itemRect.bottom - visibleBottom;
      } else if (itemRect.top < containerRect.top) {
        scrollContainer.scrollTop -= containerRect.top - itemRect.top;
      }
    });
  }

  hydrateDrawerProductImages(container) {
    container.querySelectorAll('img').forEach((img) => {
      img.loading = 'eager';
      img.decoding = 'async';

      if (img.complete) return;

      if (typeof img.decode === 'function') {
        img.decode().catch(() => {});
      }
    });
  }

  initCategorySlider(productsWrap) {
    if (!productsWrap) return;

    const slider = productsWrap.querySelector('[data-category-slider]');
    const grid = productsWrap.querySelector('.header-v2__products-grid');
    const prev = slider?.querySelector('.menu-drawer-v2__slider-arrow--prev');
    const next = slider?.querySelector('.menu-drawer-v2__slider-arrow--next');

    if (!slider || !grid || !prev || !next) return;

    if (slider.dataset.sliderInit === 'true') {
      grid.scrollLeft = 0;
    } else {
      slider.dataset.sliderInit = 'true';

      prev.addEventListener('click', () => this.scrollCategorySlider(grid, -1));
      next.addEventListener('click', () => this.scrollCategorySlider(grid, 1));

      grid.addEventListener('scroll', () => this.updateCategorySliderArrows(grid, prev, next), { passive: true });
    }

    requestAnimationFrame(() => this.updateCategorySliderArrows(grid, prev, next));
  }

  scrollCategorySlider(grid, direction) {
    const card = grid.querySelector('.header-v2__product-card');
    const scrollAmount = card ? card.offsetWidth + 10 : grid.clientWidth * 0.48;

    grid.scrollBy({
      left: direction * scrollAmount,
      behavior: 'smooth',
    });
  }

  updateCategorySliderArrows(grid, prev, next) {
    const cards = grid.querySelectorAll('.header-v2__product-card');
    const canScroll = grid.scrollWidth > grid.clientWidth + 2;
    const showNav = cards.length > 2 && canScroll;
    const maxScroll = grid.scrollWidth - grid.clientWidth;

    prev.hidden = !showNav || grid.scrollLeft <= 2;
    next.hidden = !showNav || grid.scrollLeft >= maxScroll - 2;
  }

  initRatingWidgets() {
    if (typeof window.vstarReload === 'function') {
      window.vstarReload();
    }
  }
}

customElements.define('header-drawer', HeaderDrawer);
